import { Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { CreditAgreementFullArtifactWipConfigType } from '../../../types';
import { FinancialCovenantConfig } from './FinancialCovenantConfig';
import {
  ChevronIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';

type FinancialCovenantRowProps = {
  organizationId: string;
  financialCovenantProperty: CreditAgreement.DomainEntity.FinancialCovenantProperty;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  index: number;
  basePropertyKey: string;
  fullPropertyKey: string;
  headerLength: number;
  triggerDate: Util.FullDateStr;
};
const FinancialCovenantRow = ({
  organizationId,
  financialCovenantProperty,
  wipConfig,
  index,
  basePropertyKey,
  fullPropertyKey,
  headerLength,
  triggerDate,
}: FinancialCovenantRowProps) => {
  const {
    covenantKey,
    section,
    description,
    limit,
    name,
    _config_,
  } = financialCovenantProperty;

  const [isOpen, setIsOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState<boolean>(false);

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700">
        <Td>{commonUtils.toTitleCase(name)}</Td>
        <Td>{covenantKey}</Td>
        <Td>{section ?? '-'}</Td>
        <Td>{description || '-'}</Td>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Td>
            <Flex justifyContent="center">
              <EditIconButton
                ariaLabel="edit"
                label="Edit"
                onClick={() => setIsOpen(true)}
              />
              <DeleteArrayPropertyButton
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                idx={index}
              />
            </Flex>

            {/* *** Edit Modal *** */}
            <EditPropertyModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              wipConfig={wipConfig}
              basePropertyKey={fullPropertyKey}
              modalHeaderLabel={`Financial Covenant: ${name}`}
            />
          </Td>
        )}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Td>
            <ChevronIconButton
              openState={isConfigOpen}
              setOpenState={setIsConfigOpen}
              boxSize="5"
            />
          </Td>
        )}
      </Tr>
      {isConfigOpen && (
        <FinancialCovenantConfig
          organizationId={organizationId}
          limit={limit}
          _config_={_config_}
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          headersLength={headerLength}
          triggerDate={triggerDate}
          covenantKey={covenantKey}
        />
      )}
    </>
  );
};

type FinancialCovenantTableProps = {
  organizationId: string;
  sanitizedFinancialCovenants: CreditAgreement.DomainEntity.FinancialCovenantProperty[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  triggerDate: Util.FullDateStr;
};
export const FinancialCovenantTable = ({
  organizationId,
  sanitizedFinancialCovenants,
  wipConfig,
  basePropertyKey,
  triggerDate,
}: FinancialCovenantTableProps) => {
  if (
    !sanitizedFinancialCovenants ||
    _.isEmpty(sanitizedFinancialCovenants) ||
    !Object.keys(sanitizedFinancialCovenants).length ||
    !Object.values(sanitizedFinancialCovenants).length
  ) {
    return <Text>There are currently no properties in this section.</Text>;
  }

  return (
    <>
      <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th w="18%">Covenant Key</Th>
            <Th w="10%">Section</Th>
            <Th>Description</Th>
            {wipConfig && (
              <>
                <Th w="8%" textAlign="center">
                  Action
                </Th>
                <Th w="5%" />
              </>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {_.map(
            _.toPairs(sanitizedFinancialCovenants),
            ([propertyKey, financialCovenantProperty], index) => {
              if (!financialCovenantProperty) {
                return null;
              }
              const fullPropertyKey = `${basePropertyKey}.${propertyKey}`;
              return (
                <FinancialCovenantRow
                  key={`row-${index}`}
                  organizationId={organizationId}
                  financialCovenantProperty={financialCovenantProperty}
                  index={index}
                  wipConfig={wipConfig}
                  basePropertyKey={basePropertyKey}
                  fullPropertyKey={fullPropertyKey}
                  headerLength={wipConfig ? 5 : 4}
                  triggerDate={triggerDate}
                />
              );
            },
          )}
        </Tbody>
      </Table>
    </>
  );
};
